import { useMemo } from 'react';
import { Button } from './Button';
import { LockOnIcon } from './icons';

export interface ResultCelebrationProps {
  status: 'solved' | 'failed';
  /** Solved: t('ec2.celebration.solvedToast'). Failed: t('ec2.celebration.failedTitle'). */
  title: string;
  /** Failed: the AI explanation verbatim (quote block). Solved: optional verdict line. */
  message?: string;
  /** Bucks earned (solved) — rendered as gradient display numeral. */
  amount?: number;
  /** Accessible text for the amount, e.g. t('ec2.celebration.earned', {amount}). */
  amountLabel?: string;
  /** Primary action (e.g. t('ec2.celebration.tryAgain') / t('ec2.celebration.backToQuests')). */
  actionLabel?: string;
  onAction?: () => void;
  /** Caption under the action, e.g. `2 tries left today`. */
  actionCaption?: string;
  className?: string;
}

/* Deterministic pseudo-random confetti layout (stable renders & tests). */
function confettiPieces(count: number) {
  const colors = ['var(--primary)', 'var(--bucks)', 'var(--accent)', 'var(--fg)'];
  const sizes = [6, 9, 12];
  return Array.from({ length: count }, (_, i) => {
    const golden = (i * 137.508) % 360;
    const rad = (golden * Math.PI) / 180;
    const dist = 60 + ((i * 53) % 70);
    return {
      color: colors[i % colors.length] as string,
      size: sizes[i % sizes.length] as number,
      x: Math.round(Math.cos(rad) * dist),
      y: Math.round(Math.sin(rad) * dist - 40),
      r: ((i * 97) % 360) - 180,
      delay: (i % 6) * 30,
    };
  });
}

/**
 * Quest result moment (brief §5 "Quest completion celebration" + §6 photo beat 5).
 * Solved: lock-on brackets snap in and flash --success, 24 QR-module confetti
 * squares burst with the brand's own pixels, gradient amount numeral.
 * Failed: --danger header, explanation verbatim in a quote block — no flourish.
 * Reduced motion (CSS): confetti/snap animations are disabled → static frame.
 */
export function ResultCelebration({
  status,
  title,
  message,
  amount,
  amountLabel,
  actionLabel,
  onAction,
  actionCaption,
  className = '',
}: ResultCelebrationProps) {
  const pieces = useMemo(() => confettiPieces(24), []);
  const solved = status === 'solved';

  return (
    <div
      role="status"
      data-testid="ec-result-celebration"
      data-status={status}
      className={`relative flex flex-col items-center gap-4 overflow-hidden px-6 py-10 text-center ${className}`}
    >
      {solved && (
        <div aria-hidden className="pointer-events-none absolute inset-0" data-testid="ec-confetti">
          {pieces.map((p, i) => (
            <span
              key={i}
              className="ec-confetti-piece absolute left-1/2 top-1/3 rounded-[3px]"
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                animationDelay: `${p.delay}ms`,
                ['--ec-confetti-x' as string]: `${p.x}px`,
                ['--ec-confetti-y' as string]: `${p.y}px`,
                ['--ec-confetti-r' as string]: `${p.r}deg`,
              }}
            />
          ))}
        </div>
      )}

      <span className={`ec-bracket-snap ${solved ? 'text-success' : 'text-danger'}`}>
        <LockOnIcon size={56} aria-hidden />
      </span>

      <h2 className={`font-display text-3xl leading-[1.1] font-bold tracking-[-0.02em] ${solved ? 'text-fg' : 'text-danger'}`}>
        {title}
      </h2>

      {solved && amount !== undefined && (
        <p
          role="img"
          aria-label={amountLabel ?? `+${amount}`}
          className="font-display text-[44px] leading-none font-extrabold tabular-nums"
          style={{ background: 'var(--gradient-bucks)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}
        >
          <span aria-hidden>+{amount}</span>
        </p>
      )}

      {message &&
        (solved ? (
          <p className="max-w-[40ch] text-base text-fg-secondary">{message}</p>
        ) : (
          <blockquote className="max-w-[40ch] rounded-(--radius-chip) border-l-2 border-danger bg-surface px-4 py-3 text-left text-base text-fg-secondary">
            {message}
          </blockquote>
        ))}

      {actionLabel && onAction && (
        <div className="flex flex-col items-center gap-2">
          <Button variant={solved ? 'primary' : 'ghost'} onClick={onAction}>
            {actionLabel}
          </Button>
          {actionCaption && <p className="text-[13px] font-medium text-fg-muted">{actionCaption}</p>}
        </div>
      )}
    </div>
  );
}
